import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Badge,
  Banner,
  Breadcrumbs,
  Button,
  Dialog,
  Empty,
  Input,
  Label,
  LayerCard,
  Loader,
  Select,
  Table,
  Text,
  useKumoToastManager,
} from '@cloudflare/kumo'
import {
  ArrowsClockwiseIcon,
  DownloadSimpleIcon,
  HouseIcon,
  PaperPlaneTiltIcon,
  PencilSimpleIcon,
  PowerIcon,
  PrinterIcon,
  TrashIcon,
  XIcon,
} from '@phosphor-icons/react'
import { ApiError, apiBlob, apiRequest } from '../api/client'
import {
  checkPrinterConnection,
  deactivatePrinter,
  deletePrinter,
  getPrinter,
  getPrinterJobs,
  markPrinterVerified,
  reactivatePrinter,
  runTestPrint,
  sendDocumentToPrint,
  updatePrinter,
  type PrinterDto,
  type PrinterJobDto,
  type PrinterStatus,
} from '../api/printers'
import { PrinterStatusBadge } from '../components/PrinterStatusBadge'
import { PrinterConnectionBadge } from '../components/PrinterConnectionBadge'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { DeleteResource } from '../components/kumo/delete-resource/delete-resource'
import { useHasRole } from '../auth/useRoles'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { useTranslation } from '../i18n'
import { formatDateTime } from '../lib/format'
import type { Building, PrintJobErrorCategory } from '@hub-wifi/shared'

/* ------------------------------------------------------------------ */
/*  Per-status action gating — mirrors the server matrix (design doc). */
/*                                                                     */
/*  Keeping this duplicated on the SPA is intentional: the server is   */
/*  the gate, the SPA hides actions it knows won't be allowed (UX),    */
/*  and any race the SPA loses results in a 409 surfaced through the   */
/*  catch block (mapErrorToI18n).                                      */
/* ------------------------------------------------------------------ */
const canTestPrint = (s: PrinterStatus) => s === 'pending_verification' || s === 'active'
// Active-only gate — shared by the three admin surfaces that require a
// verified, working Epson connection: QR release-page send ("Send to print"
// → QR), document-upload send ("Send to print" → Uploaded PDF), and the QR
// page download ("Download page" → QR). All three 409 server-side
// (PRINTER_NOT_ACTIVE) in any other status.
const activeOnly = (s: PrinterStatus) => s === 'active'
const canMarkVerified = (s: PrinterStatus) => s === 'pending_verification'
// Needs Epson tokens — mirrors printerService.checkConnection's gate.
const canCheckConnection = (s: PrinterStatus) =>
  s === 'pending_verification' || s === 'active' || s === 'inactive'
const canDeactivate = (s: PrinterStatus) => s === 'active'
const canReactivate = (s: PrinterStatus) => s === 'inactive'
// Delete requires the printer to already be deactivated first (Request #1,
// docs/plans/2026-07-12-printer-detail-actions.md) — mirrors
// printerService.del's PRINTER_NOT_DELETABLE gate.
const canDelete = (s: PrinterStatus) => s === 'inactive'
// Mirrors printerService.update's editable-status gate — pending_oauth and
// revoked printers can't be renamed / moved.
const canEdit = (s: PrinterStatus) =>
  s === 'pending_verification' || s === 'active' || s === 'inactive'
// F5 previously offered a "Re-onboard" CTA here for `revoked` printers,
// routing to the onboarding wizard. Dropped in Task 6 (2026-08-08): the
// printers list page's "Add printer" button (PrintersListPage.tsx) already
// routes to the same `/admin/printers/new` wizard, so the capability isn't
// lost — this page just no longer duplicates the entry point.

/**
 * Label key + Badge variant per PrintJobStatus — matches the Recent Jobs
 * table. `cancelled` is deliberately `secondary` (neutral), not
 * `destructive` — a cancellation (by the device, Epson Connect, or the
 * user) is not a printer fault. `expired` sits with the other
 * in-flight/terminal-but-benign statuses for the same reason.
 */
const STATUS_PRESENTATION: Record<
  PrinterJobDto['status'],
  { labelKey: string; badgeVariant: 'primary' | 'secondary' | 'destructive' }
> = {
  queued: { labelKey: 'printers.jobs.status.queued', badgeVariant: 'secondary' },
  releasing: { labelKey: 'printers.jobs.status.releasing', badgeVariant: 'secondary' },
  printing: { labelKey: 'printers.jobs.status.printing', badgeVariant: 'secondary' },
  completed: { labelKey: 'printers.jobs.status.completed', badgeVariant: 'primary' },
  failed: { labelKey: 'printers.jobs.status.failed', badgeVariant: 'destructive' },
  cancelled: { labelKey: 'printers.jobs.status.cancelled', badgeVariant: 'secondary' },
  expired: { labelKey: 'printers.jobs.status.expired', badgeVariant: 'secondary' },
}

/** Remediation hint i18n key per `PrintJobErrorCategory` — error detail dialog. */
const REMEDIATION_HINT_KEY: Record<PrintJobErrorCategory, string> = {
  recoverable: 'printers.jobs.detail.hint.recoverable',
  generic: 'printers.jobs.detail.hint.generic',
  expired: 'printers.jobs.detail.hint.expired',
}

/**
 * Exhaustive over the three cases that reach the error detail dialog
 * (`hasErrorDetail` gates on `failed`/`cancelled` + non-null `errorCode`):
 *  - `cancelled` → the cancelled-specific hint.
 *  - `failed` with a category → the category-specific hint. `errorCategory`
 *    is only ever set for classified Epson-side failures (see
 *    printCenter.ts).
 *  - `failed` without a category → an app-side/release failure. The release
 *    flow's catch branch (`markReleasingToFailed` in
 *    apps/api/src/repositories/printJobRepo.ts) sets `status='failed'` with
 *    an `errorCode` (e.g. `release_failed`, `print_rejected`) but never sets
 *    `errorCategory` — this is a real failure, not a cancellation, and must
 *    not fall back to the cancelled copy.
 */
const remediationHintKey = (job: PrinterJobDto): string => {
  if (job.status === 'cancelled') return 'printers.jobs.detail.hint.cancelled'
  if (job.errorCategory) return REMEDIATION_HINT_KEY[job.errorCategory]
  return 'printers.jobs.detail.hint.appSide'
}

/** Only failed/cancelled jobs with a recorded error code get a Detalhes control. */
const hasErrorDetail = (job: PrinterJobDto): boolean =>
  (job.status === 'failed' || job.status === 'cancelled') && job.errorCode !== null

export function PrinterDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toasts = useKumoToastManager()

  const [printer, setPrinter] = useState<PrinterDto | null>(null)
  const [jobs, setJobs] = useState<PrinterJobDto[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Edit dialog state.
  const [editOpen, setEditOpen] = useState(false)
  const [editName, setEditName] = useState('')
  const [editBuildingId, setEditBuildingId] = useState('')
  const [buildings, setBuildings] = useState<Building[]>([])
  const [buildingsLoaded, setBuildingsLoaded] = useState(false)
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // "Send to print" chooser dialog (Request #2) — QR release / Diagnostic /
  // Uploaded PDF.
  const [sendToPrintOpen, setSendToPrintOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // "Download page" chooser dialog (Request #3) — QR page / Diagnostic page.
  const [downloadPageOpen, setDownloadPageOpen] = useState(false)

  // Deactivate (reversible — ConfirmDialog) and Delete (destructive,
  // typed-name — DeleteResource) — both moved into the page header
  // (Request #1); the Danger zone card is gone.
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  // Printer creation and deletion stay admin-only; operators manage and
  // monitor existing printers. Hide the controls they cannot use.
  const isAdmin = useHasRole('admin')
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Recent jobs — error detail dialog (Task 4). Holds the job whose
  // Detalhes control was clicked; null means closed.
  const [detailJob, setDetailJob] = useState<PrinterJobDto | null>(null)

  // Epson Connect fields (Task 6), rendered in the Integração Epson card —
  // admin-console password is masked by default; this only ever toggles
  // local display state, never a URL, title/aria-label, or anything logged.
  const [adminPasswordRevealed, setAdminPasswordRevealed] = useState(false)

  const load = useCallback(async () => {
    if (!id) return
    try {
      const token = await getAccessTokenSilently()
      const p = await getPrinter(token, id)
      setPrinter(p)
      setError(null)
      // Recent jobs are secondary information — a failure here shouldn't
      // block the rest of the detail page from rendering.
      try {
        setJobs(await getPrinterJobs(token, id, 20))
      } catch {
        setJobs([])
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('printers.errors.generic'))
    } finally {
      setLoading(false)
    }
  }, [id, getAccessTokenSilently, t])

  useEffect(() => {
    void load()
  }, [load])

  // Map a thrown error to a localized message. Service errors carry a
  // `code` in the response body (see errorToHttp in apps/api/src/routes/
  // printers.ts) — try `printers.errors.<CODE>` first, fall back to the
  // generic message.
  const mapErrorToI18n = (err: unknown): string => {
    if (err instanceof ApiError) {
      const code = (err.body as { code?: string } | null)?.code
      if (code) {
        const i18nKey = `printers.errors.${code}`
        const localized = t(i18nKey)
        if (localized !== i18nKey) return localized
      }
      return err.message
    }
    return t('printers.errors.generic')
  }

  const doAction = async (label: string, fn: (token: string) => Promise<void>) => {
    setBusy(label)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      await fn(token)
      await load()
    } catch (err) {
      setError(mapErrorToI18n(err))
    } finally {
      setBusy(null)
    }
  }

  // Auth-gated PDF download. Opens an empty tab synchronously inside the
  // user-gesture handler (so popup blockers don't trip), then navigates it
  // to a blob URL once the authenticated fetch returns. `<a href>` won't
  // work because browser navigation can't carry the Auth0 Bearer token.
  const downloadPdf = async (kind: 'qr' | 'diagnostic', printerId: string) => {
    const tab = window.open('', '_blank', 'noopener,noreferrer')
    setBusy(kind)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      // The QR label is now an HTML page (printed from the browser); the
      // diagnostic sheet is still a PDF. Both are opened as authed blobs.
      const path = kind === 'qr' ? 'qr.html' : 'diagnostic.pdf'
      const blob = await apiBlob(`/api/admin/printers/${printerId}/${path}`, { token })
      const url = URL.createObjectURL(blob)
      if (tab) {
        tab.location.href = url
      } else {
        window.location.href = url
      }
      // Revoke after the tab has had time to load. 60s is generous; the
      // artifact is fetched into the tab by then.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err) {
      if (tab) tab.close()
      setError(mapErrorToI18n(err))
    } finally {
      setBusy(null)
    }
  }

  // "Send to print" → QR release page / Diagnostic page options. Both go
  // through the existing runTestPrint(kind) call — the dialog is purely a
  // relocation of the old header/QR-card buttons into one chooser. Success
  // toast title reuses the option's own label (matches the pre-refactor QR
  // test-print idiom) — the Diagnostic option's label is intentionally kept
  // on `runTestPrintDiagnostic` so this toast text (and the e2e selector
  // that reads it) doesn't change.
  const handleSendOption = async (kind: 'qr' | 'diagnostic') => {
    if (!printer) return
    setBusy(`send-${kind}`)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      await runTestPrint(token, printer.id, kind)
      toasts.add({
        variant: 'success',
        title: t(
          kind === 'qr'
            ? 'printers.detail.runTestPrintQr'
            : 'printers.detail.runTestPrintDiagnostic',
        ),
      })
      setSendToPrintOpen(false)
      await load()
    } catch (err) {
      setError(mapErrorToI18n(err))
    } finally {
      setBusy(null)
    }
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    setUploadFile(file)
    // Client-side pre-check — the server enforces the same PDF-only gate
    // (415 UNSUPPORTED_MEDIA) but catching an obviously-wrong pick before
    // the round trip is a better experience.
    setUploadError(
      file && file.type !== 'application/pdf' ? t('printers.detail.uploadNotPdf') : null,
    )
  }

  const handleUploadSend = async () => {
    if (!printer || !uploadFile) return
    if (uploadFile.type !== 'application/pdf') {
      setUploadError(t('printers.detail.uploadNotPdf'))
      return
    }
    setBusy('upload')
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      await sendDocumentToPrint({ token, printerId: printer.id, file: uploadFile })
      toasts.add({ variant: 'success', title: t('printers.detail.uploadSuccess') })
      setSendToPrintOpen(false)
      setUploadFile(null)
      setUploadError(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      await load()
    } catch (err) {
      setError(mapErrorToI18n(err))
    } finally {
      setBusy(null)
    }
  }

  const handleDeactivate = async () => {
    if (!printer) return
    setBusy('deactivate')
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      await deactivatePrinter(token, printer.id)
      toasts.add({ variant: 'success', title: t('printers.detail.deactivate') })
      setConfirmDeactivateOpen(false)
      await load()
    } catch (err) {
      setError(mapErrorToI18n(err))
    } finally {
      setBusy(null)
    }
  }

  const handleDelete = async () => {
    if (!printer) return
    setBusy('delete')
    setDeleteError(null)
    try {
      const token = await getAccessTokenSilently()
      await deletePrinter(token, printer.id)
      toasts.add({ variant: 'success', title: t('printers.detail.delete') })
      navigate('/admin/printers')
    } catch (err) {
      setDeleteError(mapErrorToI18n(err))
    } finally {
      setBusy(null)
    }
  }

  const openEdit = () => {
    if (!printer) return
    setEditName(printer.name)
    setEditBuildingId(printer.buildingId)
    setEditError(null)
    setEditOpen(true)
    if (!buildingsLoaded) {
      void (async () => {
        try {
          const token = await getAccessTokenSilently()
          const list = await apiRequest<Building[]>('/api/buildings', { token })
          setBuildings(list)
        } catch {
          // Building list is best-effort — the Select just stays empty and
          // the admin can still rename without changing the building.
        } finally {
          setBuildingsLoaded(true)
        }
      })()
    }
  }

  const editUnchanged =
    !printer || (editName.trim() === printer.name && editBuildingId === printer.buildingId)

  const saveEdit = async () => {
    if (!printer) return
    const patch: { name?: string; buildingId?: string } = {}
    const trimmedName = editName.trim()
    if (trimmedName !== printer.name) patch.name = trimmedName
    if (editBuildingId !== printer.buildingId) patch.buildingId = editBuildingId
    if (Object.keys(patch).length === 0) {
      setEditOpen(false)
      return
    }
    setEditSaving(true)
    setEditError(null)
    try {
      const token = await getAccessTokenSilently()
      await updatePrinter(token, printer.id, patch)
      toasts.add({ variant: 'success', title: t('printers.detail.editTitle') })
      setEditOpen(false)
      await load()
    } catch (err) {
      setEditError(mapErrorToI18n(err))
    } finally {
      setEditSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size="lg" />
      </div>
    )
  }

  if (!printer) {
    return (
      <div>
        <Banner variant="error">{error ?? t('printers.errors.NOT_FOUND')}</Banner>
        <div className="mt-2">
          <Button variant="secondary" onClick={() => navigate('/admin/printers')}>
            {t('printers.backToList')}
          </Button>
        </div>
      </div>
    )
  }

  // Epson Connect console link — the device's own web console URL. Prefer
  // the stored `adminUrl` (set by the API for devices that expose one);
  // derive the Epson Connect fleet-management URL from the serial number
  // otherwise. Renders no link at all when neither is available.
  const epsonConnectUrl = printer.adminUrl
    ? printer.adminUrl
    : printer.serialNumber
      ? `https://www.epsonconnect.com/user/Device/index?serial_number=${encodeURIComponent(printer.serialNumber)}`
      : null

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <button
              type="button"
              onClick={() => navigate('/admin/printers')}
              className="text-kumo-subtle hover:text-kumo-default text-sm"
            >
              {t('printers.title')}
            </button>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{printer.name}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={printer.name}
        titleIcon={<PrinterIcon size={24} className="text-kumo-subtle" />}
        description={
          printer.verifiedAt
            ? t('printers.detail.verifiedOn', {
                date: new Date(printer.verifiedAt).toLocaleDateString(),
              })
            : t('printers.detail.notYetVerified')
        }
      >
        <PrinterStatusBadge status={printer.status} />
        {canCheckConnection(printer.status) && (
          <>
            <PrinterConnectionBadge
              connected={printer.epsonConnected}
              checkedAt={printer.connectionCheckedAt}
            />
            <Button
              variant="secondary"
              size="sm"
              icon={<ArrowsClockwiseIcon size={16} />}
              loading={busy === 'connection'}
              disabled={busy !== null}
              onClick={() =>
                doAction('connection', async (tok) => {
                  await checkPrinterConnection(tok, printer.id)
                })
              }
            >
              {t('printers.connection.check')}
            </Button>
          </>
        )}
        {canEdit(printer.status) && (
          <Button
            variant="secondary"
            size="sm"
            icon={<PencilSimpleIcon size={16} />}
            disabled={busy !== null}
            onClick={openEdit}
          >
            {t('common.edit')}
          </Button>
        )}
        {canMarkVerified(printer.status) && (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy !== null}
            onClick={() =>
              doAction('mark', async (tok) => {
                await markPrinterVerified(tok, printer.id)
              })
            }
          >
            {t('printers.detail.markVerified')}
          </Button>
        )}
        {canReactivate(printer.status) && (
          <Button
            variant="primary"
            size="sm"
            disabled={busy !== null}
            onClick={() =>
              doAction('reactivate', async (tok) => {
                const result = await reactivatePrinter(tok, printer.id)
                if (result.status === 'needs_reoauth') {
                  // F5: server now transitions the row to `revoked` when
                  // refresh is expired — re-fetch will reflect the new
                  // status (no Re-onboard CTA on this page — see the
                  // canReonboard removal note above).
                  setError(t('printers.errors.needs_reoauth'))
                }
              })
            }
          >
            {t('printers.detail.reactivate')}
          </Button>
        )}
        {canTestPrint(printer.status) && (
          <Button
            variant="primary"
            size="sm"
            icon={<PaperPlaneTiltIcon size={16} />}
            disabled={busy !== null}
            onClick={() => setSendToPrintOpen(true)}
          >
            {t('printers.detail.sendToPrint')}
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          icon={<DownloadSimpleIcon size={16} />}
          disabled={busy !== null}
          onClick={() => setDownloadPageOpen(true)}
        >
          {t('printers.detail.downloadPage')}
        </Button>
        {canDeactivate(printer.status) && (
          <Button
            variant="secondary"
            size="sm"
            icon={<PowerIcon size={16} />}
            disabled={busy !== null}
            onClick={() => setConfirmDeactivateOpen(true)}
          >
            {t('printers.detail.deactivate')}
          </Button>
        )}
        {canDelete(printer.status) && isAdmin && (
          <Button
            variant="secondary-destructive"
            size="sm"
            icon={<TrashIcon size={16} />}
            disabled={busy !== null}
            onClick={() => {
              setDeleteError(null)
              setDeleteOpen(true)
            }}
          >
            {t('printers.detail.delete')}
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-col gap-4">
        {error && <Banner variant="error">{error}</Banner>}

        {/* Epson integration block — includes the Epson Connect device
          console link + admin console credentials (Task 6), merged into
          this card's <dl> after the pre-existing rows. Mark verified /
          Reactivate live in the header above. */}
        <LayerCard>
          <LayerCard.Primary>
            <Text as="h2" variant="heading3" DANGEROUS_className="mb-3">
              {t('printers.detail.epsonBlock')}
            </Text>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <dt className="text-kumo-subtle">{t('printers.detail.product')}</dt>
              <dd>{printer.productName ?? '—'}</dd>
              <dt className="text-kumo-subtle">{t('printers.detail.serial')}</dt>
              <dd>{printer.serialNumber ?? '—'}</dd>
              <dt className="text-kumo-subtle">{t('printers.connection.label')}</dt>
              <dd>
                <PrinterConnectionBadge
                  connected={printer.epsonConnected}
                  checkedAt={printer.connectionCheckedAt}
                />
              </dd>
              <dt className="text-kumo-subtle">{t('printers.connection.lastSeen')}</dt>
              <dd>
                {printer.lastSeenConnectedAt
                  ? new Date(printer.lastSeenConnectedAt).toLocaleString()
                  : '—'}
              </dd>
              <dt className="text-kumo-subtle">{t('printers.connection.lastChecked')}</dt>
              <dd>
                {printer.connectionCheckedAt
                  ? new Date(printer.connectionCheckedAt).toLocaleString()
                  : t('printers.never')}
              </dd>
              <dt className="text-kumo-subtle">{t('printers.detail.tokenExpires')}</dt>
              <dd>
                {printer.epsonTokenExpiresAt
                  ? new Date(printer.epsonTokenExpiresAt).toLocaleString()
                  : '—'}
              </dd>
              <dt className="text-kumo-subtle">{t('printers.detail.refreshExpires')}</dt>
              <dd>
                {printer.epsonRefreshExpiresAt
                  ? new Date(printer.epsonRefreshExpiresAt).toLocaleString()
                  : '—'}
              </dd>
              <dt className="text-kumo-subtle">{t('printers.detail.lastTestResult')}</dt>
              <dd>
                {printer.lastTestPrintError
                  ? t('printers.detail.lastTestFailed', {
                      error: printer.lastTestPrintError,
                    })
                  : printer.lastTestPrintAttemptAt
                    ? t('printers.detail.lastTestSucceeded', {
                        date: new Date(printer.lastTestPrintAttemptAt).toLocaleString(),
                      })
                    : '—'}
              </dd>
              {epsonConnectUrl && (
                <>
                  <dt className="text-kumo-subtle">{t('printers.detail.epsonConsole')}</dt>
                  <dd>
                    <a
                      href={epsonConnectUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-kumo-link underline"
                    >
                      {t('printers.detail.epsonConnectOpenDevice')}
                    </a>
                  </dd>
                </>
              )}
              {printer.adminUser !== null && (
                <>
                  <dt className="text-kumo-subtle">{t('printers.detail.epsonConnectUser')}</dt>
                  <dd>{printer.adminUser}</dd>
                </>
              )}
              {printer.adminPassword !== null && (
                <>
                  <dt className="text-kumo-subtle">{t('printers.detail.epsonConnectPassword')}</dt>
                  <dd className="flex items-center gap-2">
                    <span>{adminPasswordRevealed ? printer.adminPassword : '••••••••'}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAdminPasswordRevealed((revealed) => !revealed)}
                    >
                      {adminPasswordRevealed
                        ? t('printers.detail.epsonConnectHidePassword')
                        : t('printers.detail.epsonConnectShowPassword')}
                    </Button>
                  </dd>
                </>
              )}
            </dl>
          </LayerCard.Primary>
        </LayerCard>

        {/* Recent jobs */}
        <LayerCard>
          <LayerCard.Primary className="p-0">
            <Text as="h2" variant="heading3" DANGEROUS_className="p-3 pb-0">
              {t('printers.detail.recentJobs')}
            </Text>
            {jobs.length === 0 ? (
              <Empty size="sm" title={t('printers.detail.noJobs')} />
            ) : (
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.Head className="py-1 px-2 text-xs font-medium text-kumo-subtle bg-kumo-base">
                        {t('printers.jobs.col.company')}
                      </Table.Head>
                      <Table.Head className="py-1 px-2 text-xs font-medium text-kumo-subtle bg-kumo-base">
                        {t('printers.jobs.col.coworker')}
                      </Table.Head>
                      <Table.Head className="py-1 px-2 text-xs font-medium text-kumo-subtle bg-kumo-base">
                        {t('printers.jobs.col.createdAt')}
                      </Table.Head>
                      <Table.Head className="py-1 px-2 text-xs font-medium text-kumo-subtle bg-kumo-base">
                        {t('printers.jobs.col.printedAt')}
                      </Table.Head>
                      <Table.Head className="py-1 px-2 text-xs font-medium text-kumo-subtle bg-kumo-base">
                        {t('printers.jobs.col.status')}
                      </Table.Head>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {jobs.map((job) => (
                      <Table.Row key={job.id}>
                        <Table.Cell>
                          {job.companyId ? (
                            <Link
                              to={`/companies/${job.companyId}/edit`}
                              className="text-kumo-link underline"
                            >
                              {job.companyName ?? job.companyId}
                            </Link>
                          ) : (
                            (job.companyName ?? '—')
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {job.coworkerName ? (
                            <div className="flex flex-col">
                              <span>{job.coworkerName}</span>
                              <span className="text-xs text-kumo-subtle">{job.coworkerEmail}</span>
                            </div>
                          ) : (
                            '—'
                          )}
                        </Table.Cell>
                        <Table.Cell>{formatDateTime(job.createdAt)}</Table.Cell>
                        <Table.Cell>
                          {job.printedAt ? formatDateTime(job.printedAt) : '—'}
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex items-center gap-2">
                            <Badge variant={STATUS_PRESENTATION[job.status].badgeVariant}>
                              {t(STATUS_PRESENTATION[job.status].labelKey)}
                            </Badge>
                            {hasErrorDetail(job) && (
                              <Button variant="ghost" size="sm" onClick={() => setDetailJob(job)}>
                                {t('printers.jobs.detail.open')}
                              </Button>
                            )}
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}
          </LayerCard.Primary>
        </LayerCard>
      </div>

      <Dialog.Root
        open={editOpen}
        onOpenChange={(next) => {
          if (!next) setEditOpen(false)
        }}
      >
        <Dialog size="sm" className="p-4">
          <div className="mb-3 flex items-center justify-between gap-4">
            <Dialog.Title className="text-lg font-medium">
              {t('printers.detail.editTitle')}
            </Dialog.Title>
            <Dialog.Close
              aria-label={t('common.close')}
              render={(props) => (
                <Button
                  {...props}
                  variant="secondary"
                  shape="square"
                  size="sm"
                  icon={<XIcon />}
                  aria-label={t('common.close')}
                />
              )}
            />
          </div>

          {editError && <Banner variant="error">{editError}</Banner>}

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <Label>{t('printers.detail.nameLabel')}</Label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                maxLength={120}
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label>{t('printers.detail.buildingLabel')}</Label>
              <Select
                value={editBuildingId}
                onValueChange={(v) => setEditBuildingId(v ?? '')}
                items={buildings.map((b) => ({ label: `${b.name} - ${b.city}`, value: b.id }))}
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditOpen(false)}>
              {t('printers.detail.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={editSaving || !editName.trim() || editUnchanged}
              onClick={() => void saveEdit()}
            >
              {t('common.save')}
            </Button>
          </div>
        </Dialog>
      </Dialog.Root>

      {/* "Send to print" chooser — Request #2. Three options: QR release
        page + Diagnostic page (both existing runTestPrint kinds, relocated
        here from the old header button / QR label card) and a new Uploaded
        PDF option that posts an admin-chosen file straight to Epson. */}
      <Dialog.Root
        open={sendToPrintOpen}
        onOpenChange={(next) => {
          setSendToPrintOpen(next)
          if (!next) {
            setUploadFile(null)
            setUploadError(null)
          }
        }}
      >
        <Dialog size="sm" className="p-4">
          <div className="mb-3 flex items-center justify-between gap-4">
            <Dialog.Title className="text-lg font-medium">
              {t('printers.detail.sendToPrintTitle')}
            </Dialog.Title>
            <Dialog.Close
              aria-label={t('common.close')}
              render={(props) => (
                <Button
                  {...props}
                  variant="secondary"
                  shape="square"
                  size="sm"
                  icon={<XIcon />}
                  aria-label={t('common.close')}
                />
              )}
            />
          </div>

          <div className="flex flex-col gap-3">
            {/* (a) QR release page */}
            <div className="flex flex-col gap-1">
              <Button
                variant="secondary"
                className="w-full"
                disabled={busy !== null || !activeOnly(printer.status)}
                onClick={() => void handleSendOption('qr')}
              >
                {t('printers.detail.sendOptionQr')}
              </Button>
              {!activeOnly(printer.status) && (
                <Text as="p" size="sm" variant="secondary">
                  {t('printers.detail.qrActiveOnly')}
                </Text>
              )}
            </div>

            {/* (b) Diagnostic page — label kept on `runTestPrintDiagnostic`
              (not `sendOptionDiagnostic`) so the e2e selector that matches
              this text keeps working across the relocation. */}
            <Button
              variant="secondary"
              className="w-full"
              disabled={busy !== null || !canTestPrint(printer.status)}
              onClick={() => void handleSendOption('diagnostic')}
            >
              {t('printers.detail.runTestPrintDiagnostic')}
            </Button>

            {/* (c) Uploaded PDF */}
            <div className="flex flex-col gap-2 rounded-md border border-kumo-line p-3">
              <Text as="p" size="sm" DANGEROUS_className="font-medium">
                {t('printers.detail.sendOptionUpload')}
              </Text>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={handleFileChange}
              />
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy !== null || !activeOnly(printer.status)}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t('printers.detail.uploadChoose')}
                </Button>
                {uploadFile && (
                  <Text as="span" size="sm" variant="secondary">
                    {uploadFile.name}
                  </Text>
                )}
              </div>
              {uploadError && (
                <Text as="p" size="sm" DANGEROUS_className="text-kumo-danger">
                  {uploadError}
                </Text>
              )}
              {!activeOnly(printer.status) ? (
                <Text as="p" size="sm" variant="secondary">
                  {t('printers.detail.qrActiveOnly')}
                </Text>
              ) : (
                <Text as="p" size="sm" variant="secondary">
                  {t('printers.detail.uploadHint')}
                </Text>
              )}
              <div>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={
                    busy !== null || !uploadFile || !!uploadError || !activeOnly(printer.status)
                  }
                  onClick={() => void handleUploadSend()}
                >
                  {t('printers.detail.uploadSend')}
                </Button>
              </div>
            </div>
          </div>
        </Dialog>
      </Dialog.Root>

      {/* "Download page" chooser — Request #3. */}
      <Dialog.Root
        open={downloadPageOpen}
        onOpenChange={(next) => {
          if (!next) setDownloadPageOpen(false)
        }}
      >
        <Dialog size="sm" className="p-4">
          <div className="mb-3 flex items-center justify-between gap-4">
            <Dialog.Title className="text-lg font-medium">
              {t('printers.detail.downloadPageTitle')}
            </Dialog.Title>
            <Dialog.Close
              aria-label={t('common.close')}
              render={(props) => (
                <Button
                  {...props}
                  variant="secondary"
                  shape="square"
                  size="sm"
                  icon={<XIcon />}
                  aria-label={t('common.close')}
                />
              )}
            />
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <Button
                variant="secondary"
                className="w-full"
                disabled={busy !== null || !activeOnly(printer.status)}
                onClick={() => {
                  setDownloadPageOpen(false)
                  void downloadPdf('qr', printer.id)
                }}
              >
                {t('printers.detail.qrDownload')}
              </Button>
              {!activeOnly(printer.status) && (
                <Text as="p" size="sm" variant="secondary">
                  {t('printers.detail.qrActiveOnly')}
                </Text>
              )}
            </div>
            <Button
              variant="secondary"
              className="w-full"
              disabled={busy !== null}
              onClick={() => {
                setDownloadPageOpen(false)
                void downloadPdf('diagnostic', printer.id)
              }}
            >
              {t('printers.detail.diagnosticDownload')}
            </Button>
          </div>
        </Dialog>
      </Dialog.Root>

      {/* Recent jobs — error detail dialog (Task 4). Shows the raw Epson
        error alongside a remediation hint keyed off errorCategory. */}
      <Dialog.Root
        open={detailJob !== null}
        onOpenChange={(next) => {
          if (!next) setDetailJob(null)
        }}
      >
        <Dialog size="sm" className="p-4">
          {detailJob && (
            <>
              <div className="mb-3 flex items-center justify-between gap-4">
                <Dialog.Title className="text-lg font-medium">
                  {t('printers.jobs.detail.title')}
                </Dialog.Title>
                <Dialog.Close
                  aria-label={t('common.close')}
                  render={(props) => (
                    <Button
                      {...props}
                      variant="secondary"
                      shape="square"
                      size="sm"
                      icon={<XIcon />}
                      aria-label={t('common.close')}
                    />
                  )}
                />
              </div>

              <dl className="grid grid-cols-2 gap-2 text-sm">
                <dt className="text-kumo-subtle">{t('printers.jobs.col.status')}</dt>
                <dd>{t(STATUS_PRESENTATION[detailJob.status].labelKey)}</dd>
                <dt className="text-kumo-subtle">{t('printers.jobs.detail.fileName')}</dt>
                <dd>{detailJob.fileName}</dd>
                <dt className="text-kumo-subtle">{t('printers.jobs.detail.errorCode')}</dt>
                <dd>{detailJob.errorCode ?? '—'}</dd>
                <dt className="text-kumo-subtle">{t('printers.jobs.detail.errorMessage')}</dt>
                <dd>{detailJob.errorMessage ?? '—'}</dd>
                <dt className="text-kumo-subtle">{t('printers.jobs.detail.epsonJobId')}</dt>
                <dd>{detailJob.epsonJobId ?? '—'}</dd>
                <dt className="text-kumo-subtle">{t('printers.jobs.col.createdAt')}</dt>
                <dd>{formatDateTime(detailJob.createdAt)}</dd>
                <dt className="text-kumo-subtle">{t('printers.jobs.col.printedAt')}</dt>
                <dd>{detailJob.printedAt ? formatDateTime(detailJob.printedAt) : '—'}</dd>
              </dl>

              <Text as="p" size="sm" DANGEROUS_className="mt-4 text-kumo-subtle">
                {t(remediationHintKey(detailJob))}
              </Text>
            </>
          )}
        </Dialog>
      </Dialog.Root>

      <ConfirmDialog
        open={confirmDeactivateOpen}
        onOpenChange={setConfirmDeactivateOpen}
        title={t('printers.detail.deactivateConfirmTitle')}
        description={t('printers.detail.deactivateConfirmBody')}
        confirmLabel={t('printers.detail.deactivate')}
        variant="destructive"
        loading={busy === 'deactivate'}
        onConfirm={() => void handleDeactivate()}
      />

      <DeleteResource
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        resourceType={t('deleteResource.type.printer')}
        resourceName={printer.name}
        isDeleting={busy === 'delete'}
        errorMessage={deleteError ?? undefined}
        warningNote={t('printers.detail.deleteWarningNote')}
        onDelete={() => void handleDelete()}
      />
    </div>
  )
}
